import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Container,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from "@mui/material";
import PayaAddCardForm from "../../components/PayaAddCard/PayaAddCardForm";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import showToast from "../../components/Toast/Toster";
import CardPayment from "../../Views/Admin/Invoice/Payment/CardPayment";
import AchPayment from "../../Views/Admin/Invoice/Payment/AchPayment";
import ApplePayPayment from "../../Views/Admin/Invoice/Payment/ApplePayPayment";
import GooglePayPayment from "../../Views/Admin/Invoice/Payment/GooglePayPayment";
import AxiosInstance from "../AxiosInstance";
import InputText from "../../components/InputFields/InputText";
import DollerInput from "../../components/InputFields/Doller";
import { handleAuth } from "../../components/Login/Auth";
import { keyframes } from "@mui/system";
import * as gatewayHelper from "../../utils/gatewayHelper";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import FavoriteIcon from "@mui/icons-material/Favorite";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
const primaryBlue = "#29B3E2";
const accentOrange = "#66D59A";

const slideInAnimation = keyframes`
  0% { transform: translateX(-20px); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;

const fadeInAnimation = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  boxShadow: "var(--shadow-xl)",
  padding: theme.spacing(4),
  background: "#ffffff",
  border: "1px solid rgba(6, 49, 100, 0.1)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${primaryBlue} 50%, ${accentOrange} 50%)`,
    transition: "all 0.4s ease",
  },
}));

const DonationAmountButton = styled(Button)(({ theme, selected }) => ({
  borderRadius: "16px",
  padding: theme.spacing(2),
  minWidth: "120px",
  border: `2px solid ${selected ? accentOrange : "#e0e0e0"}`,
  backgroundColor: selected ? "rgba(212, 148, 58, 0.1)" : "transparent",
  color: selected ? accentOrange : primaryBlue,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  fontWeight: 600,
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(212, 148, 58, 0.1)",
    transform: "scaleX(0)",
    transformOrigin: "right",
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  "&:hover": {
    borderColor: accentOrange,
    transform: "scale(1.02)",
    "&::after": {
      transform: "scaleX(1)",
      transformOrigin: "left",
    },
  },
}));

const ImpactCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "20px",
  background: "#ffffff",
  border: "1px solid rgba(6, 49, 100, 0.1)",
  marginBottom: theme.spacing(3),
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "4px",
    height: "100%",
    background: primaryBlue,
    transition: "all 0.3s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "var(--shadow-lg)",
    "&::before": {
      background: accentOrange,
      width: "6px",
    },
  },
}));

const ImpactTitle = styled(Typography)(({ theme }) => ({
  color: primaryBlue,
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontSize: "1.2rem",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-8px",
    left: 0,
    width: "40px",
    height: "3px",
    background: accentOrange,
    transition: "width 0.3s ease",
  },
  "&:hover::after": {
    width: "60px",
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  borderRadius: "16px",
  padding: theme.spacing(2),
  background: primaryBlue,
  color: "white",
  fontSize: "1.1rem",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "var(--shadow-md)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: accentOrange,
    opacity: 0,
    transition: "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "var(--shadow-lg)",
    background: accentOrange,
    "& span": {
      color: "white",
    },
  },
  "& span": {
    position: "relative",
    zIndex: 1,
    transition: "color 0.3s ease",
  },
  "&:disabled": {
    background: theme.palette.grey[400],
    transform: "none",
    boxShadow: "none",
    "&::before": {
      display: "none",
    },
  },
}));

const HelpBox = styled(Box)(({ theme }) => ({
  mt: theme.spacing(4),
  p: theme.spacing(3),
  borderRadius: "20px",
  border: "1px solid rgba(6, 49, 100, 0.1)",
  borderLeft: `6px solid ${accentOrange}`,
  boxShadow: "var(--shadow-md)",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  "&:hover": {
    borderLeft: `8px solid ${primaryBlue}`,
    transform: "translateY(-2px) scale(1.02)",
  },
}));

const StyledDropdown = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  "& .dropdown-toggle": {
    width: "100%",
    border: "2px solid rgba(6, 49, 100, 0.2)",
    backgroundColor: "#ffffff",
    height: "44px",
    textAlign: "start",
    color: primaryBlue,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 20px",
    borderRadius: "16px",
    fontWeight: 500,
    fontSize: "1rem",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      borderColor: accentOrange,
      boxShadow: "var(--shadow-md)",
      transform: "translateY(-1px)",
    },
    "&:focus": {
      outline: "none",
      borderColor: accentOrange,
      boxShadow: "var(--focus-ring-amber)",
    },
    "&.error-state": {
      borderColor: "#dc3545 !important",
      "&:hover": {
        borderColor: "#dc3545 !important",
      },
      "&:focus": {
        borderColor: "#dc3545 !important",
      },
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(135deg, rgba(41, 179, 226, 0.05) 0%, rgba(102, 213, 154, 0.05) 100%)",
      opacity: 0,
      transition: "opacity 0.3s ease",
    },
    "&:hover::before": {
      opacity: 1,
    },
  },
  "& .dropdown-menu": {
    width: "100%",
    maxHeight: "250px",
    overflowY: "auto",
    boxShadow: "var(--shadow-xl)",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    marginTop: "8px",
    padding: "8px",
    border: "1px solid rgba(6, 49, 100, 0.08)",
    animation: `${fadeInAnimation} 0.2s ease-out`,
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: primaryBlue,
      borderRadius: "3px",
      "&:hover": {
        background: accentOrange,
      },
    },
  },
  "& .dropdown-item": {
    padding: "14px 20px",
    borderRadius: "12px",
    margin: "2px 0",
    cursor: "pointer",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    color: primaryBlue,
    fontWeight: 500,
    fontSize: "0.95rem",
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: "rgba(41, 179, 226, 0.08)",
      borderColor: "rgba(41, 179, 226, 0.2)",
      transform: "translateX(4px)",
      color: accentOrange,
    },
    "&.header": {
      backgroundColor: "rgba(6, 49, 100, 0.05)",
      color: primaryBlue,
      fontWeight: 700,
      fontSize: "0.9rem",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      borderRadius: "8px",
      marginBottom: "8px",
      "&:hover": {
        backgroundColor: "rgba(6, 49, 100, 0.08)",
        transform: "none",
        color: primaryBlue,
      },
    },
    "&.disabled": {
      color: "#9CA3AF",
      cursor: "not-allowed",
      backgroundColor: "#F3F4F6",
      "&:hover": {
        backgroundColor: "#F3F4F6",
        transform: "none",
        color: "#9CA3AF",
      },
    },
  },
  "& .dropdown-arrow": {
    color: primaryBlue,
    transition: "all 0.3s ease",
    fontSize: "1.2rem",
    "&.open": {
      transform: "rotate(180deg)",
      color: accentOrange,
    },
  },
}));

const MakeDonation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("Credit/Debit Card");
  const [cardFeeConfig, setCardFeeConfig] = useState(null);
  const [cardFeeLoading, setCardFeeLoading] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [companyData, setCompanyData] = useState(undefined);

  const presetAmounts = [25, 50, 100, 250, 500, 1000];

  const [tokenDecode, setTokenDecode] = useState({});
  
  // Recurring donation states
  const [donationType, setDonationType] = useState("once"); // "once" or "recurring"
  const [frequencyDropdownOpen, setFrequencyDropdownOpen] = useState(false);
  const [weekdayDropdownOpen, setWeekdayDropdownOpen] = useState(false);

  // Fetch card fee configuration (similar to POS, BillingHistory, and InvoicePayment)
  const fetchCardFeeConfig = async (companyId) => {
    if (!companyId) return;

    try {
      setCardFeeLoading(true);
      const response = await AxiosInstance.get(
        `/v1/card-fee/company/${companyId}`
      );

      if (response?.data?.statusCode === 200) {
        setCardFeeConfig(response.data.data);
      } else {
        // Default to Cash Discount if no config found
        setCardFeeConfig({
          PricingStructure: "Cash Discount",
          FeeLabel: "",
          FeeType: "",
          FeeAmount: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching card fee config:", error);
      // Default to Cash Discount on error
      setCardFeeConfig({
        PricingStructure: "Cash Discount",
        FeeLabel: "",
        FeeType: "",
        FeeAmount: 0,
      });
    } finally {
      setCardFeeLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const res = await handleAuth(navigate, location);
      setTokenDecode(res.data);
      setCompanyData(res.data);
      // Fetch card fee config when company data is available
      if (res.data?.CompanyId) {
        await fetchCardFeeConfig(res.data.CompanyId);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const gateway = useMemo(
    () => gatewayHelper.getGatewayForPaymentMethod(tokenDecode, paymentMethod),
    [tokenDecode, paymentMethod]
  );

  // Flags from token decode (gateway-keys IsDelete: false): hide Card/ACH when disabled
  const creditCardEnabled = tokenDecode?.creditCardEnabled !== false;
  const achEnabled = tokenDecode?.achEnabled === true;

  // When tokenDecode first loads, if current payment method is hidden by flags, switch once (ref prevents loop)
  const hasCorrectedPaymentMethodRef = useRef(false);
  useEffect(() => {
    if (!tokenDecode?.CompanyId) {
      hasCorrectedPaymentMethodRef.current = false;
      return;
    }
    if (hasCorrectedPaymentMethodRef.current) return;
    const currentHidden =
      (paymentMethod === "Credit/Debit Card" && !creditCardEnabled) ||
      (paymentMethod === "ACH Bank Payment" && !achEnabled);
    if (!currentHidden) return;
    hasCorrectedPaymentMethodRef.current = true;
    const firstAvailable = creditCardEnabled
      ? "Credit/Debit Card"
      : achEnabled
        ? "ACH Bank Payment"
        : null;
    if (firstAvailable) {
      setPaymentMethod(firstAvailable);
      donationFormik?.setFieldValue("method", firstAvailable);
    }
  }, [tokenDecode?.CompanyId, creditCardEnabled, achEnabled, paymentMethod]);

  const donationFormik = useFormik({
    initialValues: {
      amount: "25",
      method: "Credit/Debit Card",
      date: dayjs(new Date()).format("MM/DD/YYYY"),
      customer_vault_id: "",
      billing_id: "",
      token: "",
      description: "",
      // Card details
      card_type: "",
      cc_number: "",
      // Recurring donation fields
      frequency: "",
      frequency_interval: "",
      day_of_month: "",
      weekday: "",
      month: "",
      day_of_year: "",
      days_after_quarter: "",
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object().shape({
      amount: Yup.number()
        .required("Amount is required")
        .positive("Amount must be positive")
        .min(1, "Minimum donation amount is $1"),
      method: Yup.string().required("Payment method is required"),
      billing_id: Yup.string().when(["method", "token"], {
        is: (method, token) =>
          ["Credit/Debit Card", "ACH Bank Payment"].includes(method) && !token,
        then: (schema) => schema.required("Payment details are required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      customer_vault_id: Yup.string().when(["method", "token"], {
        is: (method, token) =>
          ["Credit/Debit Card", "ACH Bank Payment"].includes(method) && !token,
        then: (schema) => schema.required("Payment details are required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      token: Yup.string().when("method", {
        is: (method) => method === "Credit/Debit Card",
        then: (schema) => schema,
        otherwise: (schema) => schema.notRequired(),
      }),
      // Recurring donation validations - these will be validated manually in onSubmit
      frequency: Yup.string(),
      frequency_interval: Yup.string(),
      day_of_month: Yup.string(),
      weekday: Yup.string(),
      month: Yup.string(),
      day_of_year: Yup.string(),
      days_after_quarter: Yup.string(),
    }),
    onSubmit: async (values, { resetForm }) => {
      setSubmitAttempted(true);
      // Manually touch required fields
      donationFormik.setFieldTouched("amount", true);
      
      // If recurring, validate recurring fields
      if (donationType === "recurring") {
        // Validate card selection for recurring donations
        if (gateway !== "DEJAVOO" && (!values.billing_id || values.billing_id === "")) {
          donationFormik.setFieldTouched("billing_id", true);
          donationFormik.setFieldError("billing_id", "Card is required for recurring donations");
          setLoading(false);
          return;
        }
        
        donationFormik.setFieldTouched("frequency", true);
        if (!values.frequency || values.frequency === "") {
          donationFormik.setFieldError("frequency", "Frequency is required");
          setLoading(false);
          return;
        }
        
        if (values.frequency === "Every N Months" || values.frequency === "Every N Weeks") {
          donationFormik.setFieldTouched("frequency_interval", true);
          if (!values.frequency_interval || values.frequency_interval === "") {
            donationFormik.setFieldError("frequency_interval", "Frequency interval is required");
            setLoading(false);
            return;
          }
        }
        if (values.frequency === "Monthly" || values.frequency === "Every N Months") {
          donationFormik.setFieldTouched("day_of_month", true);
          if (!values.day_of_month || values.day_of_month === "") {
            donationFormik.setFieldError("day_of_month", "Day of month is required");
            setLoading(false);
            return;
          }
        }
        if (values.frequency === "Weekly" || values.frequency === "Every N Weeks") {
          donationFormik.setFieldTouched("weekday", true);
          if (!values.weekday || values.weekday === "") {
            donationFormik.setFieldError("weekday", "Weekday is required");
            setLoading(false);
            return;
          }
        }
        if (values.frequency === "Yearly") {
          donationFormik.setFieldTouched("day_of_year", true);
          donationFormik.setFieldTouched("month", true);
          if (!values.day_of_year || values.day_of_year === "") {
            donationFormik.setFieldError("day_of_year", "Day of year is required");
            setLoading(false);
            return;
          }
          if (!values.month || values.month === "") {
            donationFormik.setFieldError("month", "Month is required");
            setLoading(false);
            return;
          }
        }
        if (values.frequency === "Quarterly") {
          donationFormik.setFieldTouched("days_after_quarter", true);
          if (values.days_after_quarter === "" || values.days_after_quarter === null || values.days_after_quarter === undefined) {
            donationFormik.setFieldError("days_after_quarter", "Days after quarter is required");
            setLoading(false);
            return;
          }
        }
      }
      
      // One-time donation: validate card/ACH selection before proceeding
      if (donationType !== "recurring") {
        if (values.method === "Credit/Debit Card") {
          const hasBillingId = values.billing_id != null && values.billing_id !== "";
          const isDejavoo = (gateway || tokenDecode?.Gateway || "").toString().toUpperCase() === "DEJAVOO";
          const hasCard = hasBillingId || selectedCard || (isDejavoo ? (values?.token || selectedCard?.token) : true);
          if (!hasBillingId && !selectedCard) {
            donationFormik.setFieldTouched("billing_id", true);
            donationFormik.setFieldError("billing_id", "Please select a payment card.");
            showToast.error("Please select a payment card.");
            setLoading(false);
            return;
          }
          if (isDejavoo && !(values?.token || selectedCard?.token)) {
            donationFormik.setFieldTouched("billing_id", true);
            donationFormik.setFieldError("billing_id", "Please select a payment card.");
            showToast.error("Please select a payment card.");
            setLoading(false);
            return;
          }
        }
        if (values.method === "ACH Bank Payment") {
          if (!values?.billing_id || values.billing_id === "") {
            donationFormik.setFieldTouched("billing_id", true);
            donationFormik.setFieldError("billing_id", "Please select an ACH account.");
            showToast.error("Please select an ACH account.");
            setLoading(false);
            return;
          }
        }
      }
      
      // Check for validation errors
      const errors = await donationFormik.validateForm();
      if (Object.keys(errors).length > 0) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        let response;

        // If recurring donation, create recurring payment
        if (donationType === "recurring") {
          // Validate required fields before submission
          if (!values.frequency) {
            showToast.error("Please select a frequency");
            setLoading(false);
            return;
          }
          
          if (tokenDecode?.Gateway !== "DEJAVOO" && !values.billing_id) {
            showToast.error("Please select a payment card");
            setLoading(false);
            return;
          }

            // Get card_type and cc_number from selectedCard if not in formik values
            const cardType = values?.card_type || selectedCard?.cardType || selectedCard?.card_type || selectedCard?.cc_type || "";
            const ccNumber = values?.cc_number || selectedCard?.cc_number || selectedCard?.ccnumber || "";
            
            const recurringData = {
              billing_id: values?.billing_id || "",
              card_type: cardType,
              customer_vault_id: values?.customer_vault_id || "",
              cc_number: ccNumber,
              amount: parseFloat(values?.amount),
              CompanyId: tokenDecode?.CompanyId,
              CustomerId: tokenDecode?.UserId,
              frequency: values.frequency,
              frequency_interval: values.frequency_interval || "",
              day_of_month: values.day_of_month || "",
              weekday: values.weekday || "",
              month: values.month || "",
              day_of_year: values.day_of_year || "",
              days_after_quarter: values.days_after_quarter || "",
              description: values.description || "",
              Gateway: gateway || tokenDecode?.Gateway || "NMI",
              token: values?.token || "",
              DejavooToken: values?.token || "",
              IsDonation: true,
              IsTransactionFeePercentage: false,
            };

            response = await AxiosInstance.post(
              "/v1/recurring-payment/add-cards",
              recurringData
            );
            
          if (response?.data?.statusCode === 200) {
            showToast.success("Recurring donation set up successfully!");
            resetForm();
            setDonationType("once");
            setLoading(false);
            setSubmitAttempted(false);
            // Navigate to recurring pledges page
            const pathParts = location.pathname.split('/');
            const companyUrl = pathParts[1] || tokenDecode?.CompanyUrl || '';
            if (companyUrl) {
              navigate(`/${companyUrl}/donors/recurring-pledges`);
            } else {
              // Fallback navigation
              navigate('/recurring-pledges');
            }
            return;
          } else {
            showToast.error(
              response?.data?.message || response?.data?.error || "Failed to set up recurring donation."
            );
            setLoading(false);
            setSubmitAttempted(false);
            return;
          }
        }

        // Calculate totals with fee using Cash Discount/Surcharge logic
        const totals = calculatePaymentTotals(values?.amount, values?.method);
        // If TransactionFee is disabled, use original amount; otherwise use finalTotal (which includes fee)
        const finalAmount = values?.method === "Credit/Debit Card" 
          ? (tokenDecode?.TransactionFee ? totals.finalTotal : totals.cashTotal)
          : totals.cashTotal;
        const convenienceFee = totals.convenienceFee;

        if (values.method === "Apple Pay") {
          const paymentDetails = {
            paymentDetails: {
              date: values.date,
              amount: values.amount,
              CompanyId: tokenDecode?.CompanyId,
              CustomerId: tokenDecode?.UserId,
              method: "Apple Pay",
              CreatedFrom: "Donation-Page",
            },
          };

          response = await AxiosInstance.post(
            "/v1/nmi/apple-pay-payment",
            paymentDetails
          );
        } else if (values.method === "Google Pay") {
          const paymentDetails = {
            paymentDetails: {
              date: values.date,
              amount: values.amount,
              CompanyId: tokenDecode?.CompanyId,
              CustomerId: tokenDecode?.UserId,
              method: "Google Pay",
              CreatedFrom: "Donation-Page",
            },
          };

          response = await AxiosInstance.post(
            "/v1/nmi/google-pay-payment",
            paymentDetails
          );
        } else if (values.method === "Credit/Debit Card") {
          // Dejavoo card-on-file: backend requires paymentData.paymentToken
          const isDejavoo = (gateway || tokenDecode?.Gateway || "").toString().toUpperCase() === "DEJAVOO";
          const cardToken = values?.token || selectedCard?.token;
          if (isDejavoo && !cardToken) {
            showToast.error("Please select a card to donate with.");
            setLoading(false);
            return;
          }
          // Dejavoo vault/billing ids are strings (e.g. dejavoo_...); NMI are numeric - keep string to avoid NaN
          const billingId = values?.billing_id != null && typeof values.billing_id === "string" && values.billing_id.trim() !== "" && !/^\d+$/.test(String(values.billing_id).trim()) ? values.billing_id : Number(values?.billing_id);
          const customerVaultId = values?.customer_vault_id != null && typeof values.customer_vault_id === "string" && values.customer_vault_id.trim() !== "" && !/^\d+$/.test(String(values.customer_vault_id).trim()) ? values.customer_vault_id : Number(values?.customer_vault_id);
          const paymentData = {
            paymentData: {
              billing_id: billingId,
              customer_vault_id: customerVaultId,
              date: values?.date,
              amount: finalAmount, // Amount with fee - this is what gets charged
              originalAmount: parseFloat(values?.amount), // Original amount before fee
              actualAmount: finalAmount, // Amount with fee
              CompanyId: tokenDecode?.CompanyId,
              CustomerId: tokenDecode?.UserId,
              method: "Card",
              CreatedFrom: "Donation-Page",
              paymentMethod:"card_on_file",
              paymentDoingFor:"Customer-Make-Payment",
              paymentSource: "Donor Make Donation",
              paymentToken: cardToken,
              token: cardToken,
              account_name: values?.account_name,
              // Only include fee fields if customer has TransactionFee enabled
              ...(tokenDecode?.TransactionFee && convenienceFee > 0 ? {
                convenienceFee: convenienceFee,
                transactionFee: convenienceFee, // Also send as transactionFee for display
              } : {}),
            },
          };

          response = await AxiosInstance.post(
            "/v1/payment/process-payment",
            paymentData
          );
        } else if (values.method === "ACH Bank Payment") {
          if (!values?.billing_id) {
            showToast.error("Please select an ACH account.");
            setLoading(false);
            return;
          }

          const achBillingId = values?.billing_id != null && typeof values.billing_id === "string" && values.billing_id.trim() !== "" && !/^\d+$/.test(String(values.billing_id).trim()) ? values.billing_id : Number(values?.billing_id);
          const achVaultId = values?.customer_vault_id != null && typeof values.customer_vault_id === "string" && values.customer_vault_id.trim() !== "" && !/^\d+$/.test(String(values.customer_vault_id).trim()) ? values.customer_vault_id : Number(values?.customer_vault_id);
          const paymentData = {
            paymentData: {
              billing_id: achBillingId,
              customer_vault_id: achVaultId,
              date: values?.date,
              amount: values?.amount, // ACH does not have convenience fee
              CompanyId: tokenDecode?.CompanyId,
              CustomerId: tokenDecode?.UserId,
              paymentMethod:"ach",
              paymentDoingFor:"Customer-Make-Payment",
              paymentSource: "Donor Make Donation",
              method: "ACH",
              CreatedFrom: "Donation-Page",
            },
          };

          response = await AxiosInstance.post(
            "/v1/payment/process-payment",
            paymentData
          );
        }


        const GENERIC_FAILURE =
          "Payment failed. Please try again or use a different payment method.";
        const succeeded = response?.data?.data?.success === true;

        if (succeeded) {
          const msg =
            response?.data?.message ||
            response?.data?.data?.message ||
            response?.data?.data?.responseMessage ||
            response?.data?.data?.responsetext ||
            "Donation processed successfully.";
          showToast.success(msg);
          // Only reset the form on a real success — previously the finally
          // block reset even on failure, wiping the donor's amount/selection
          // and making the failure feel like "nothing happened".
          resetForm();
        } else {
          const msg =
            response?.data?.message ||
            response?.data?.data?.message ||
            response?.data?.data?.responsetext ||
            response?.data?.data?.responseMessage ||
            GENERIC_FAILURE;
          showToast.error(msg);
        }
        setLoading(false);
        setSubmitAttempted(false);
      } catch (error) {
        const GENERIC_FAILURE =
          "Payment failed. Please try again or use a different payment method.";
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.data?.responseMessage ||
          error?.response?.data?.data?.responsetext ||
          error?.response?.data?.error ||
          error?.message ||
          GENERIC_FAILURE;
        showToast.error(msg);
        setLoading(false);
        setSubmitAttempted(false);
      }
    },
  });

  useEffect(() => {
    if (donationFormik?.values?.method) {
      setPaymentMethod(donationFormik.values.method);
    }
  }, [donationFormik?.values?.method]);

  // Calculate card fee and totals using Cash Discount/Surcharge logic (similar to InvoicePayment and CustomerPayment)
  // Respects customer-level TransactionFee setting
  const calculatePaymentTotals = (amountValue = null, paymentMethodValue = null) => {
    // Use provided values or fall back to formik values
    const amount = parseFloat(amountValue !== null ? amountValue : donationFormik.values.amount) || 0;
    const method = paymentMethodValue !== null ? paymentMethodValue : paymentMethod;
    
    let convenienceFee = 0;
    let cashPrice = amount;
    let cardPrice = amount;
    let finalTotal = amount;

    if (
      tokenDecode?.TransactionFee &&
      method === "Credit/Debit Card" &&
      cardFeeConfig?.FeeAmount > 0
    ) {
      const pricingStructure = cardFeeConfig?.PricingStructure || "Cash Discount";
      
      if (pricingStructure === "Surcharge") {
        // SURCHARGE METHOD:
        // 1. Calculate convenience fee on subtotal
        if (cardFeeConfig.FeeType === "Percentage") {
          convenienceFee = (amount * cardFeeConfig.FeeAmount) / 100;
        } else if (cardFeeConfig.FeeType === "Fixed") {
          convenienceFee = cardFeeConfig.FeeAmount;
        }
        
        // 2. Total = subtotal + convenience fee
        finalTotal = amount + convenienceFee;
        cardPrice = amount + convenienceFee;
      } else {
        // CASH DISCOUNT METHOD:
        // 1. Calculate convenience fee on subtotal (cash price)
        if (cardFeeConfig.FeeType === "Percentage") {
          convenienceFee = (amount * cardFeeConfig.FeeAmount) / 100;
        } else if (cardFeeConfig.FeeType === "Fixed") {
          convenienceFee = cardFeeConfig.FeeAmount;
        }
        
        // 2. Card price = cash price + convenience fee
        cardPrice = amount + convenienceFee;
        
        // 3. Total = card price
        finalTotal = cardPrice;
      }
    } else {
      finalTotal = amount;
    }

    return {
      subtotal: amount,
      tax: 0,
      subtotalWithTax: amount,
      convenienceFee: convenienceFee,
      cashPrice: cashPrice,
      cardPrice: cardPrice,
      cardTotal: finalTotal,
      cashTotal: amount,
      finalTotal: finalTotal,
      showConvenienceFee:
        tokenDecode?.TransactionFee &&
        method === "Credit/Debit Card" &&
        cardFeeConfig?.FeeAmount > 0,
      isCashDiscount:
        tokenDecode?.TransactionFee &&
        method === "Credit/Debit Card" &&
        cardFeeConfig?.PricingStructure === "Cash Discount",
      isSurcharge:
        tokenDecode?.TransactionFee &&
        method === "Credit/Debit Card" &&
        cardFeeConfig?.PricingStructure === "Surcharge",
    };
  };

  // Recalculate totals when amount, payment method, or card fee config changes
  useEffect(() => {
    // This will trigger re-render when amount or payment method changes
  }, [donationFormik.values.amount, paymentMethod, cardFeeConfig, tokenDecode?.TransactionFee]);

  // For recurring donations, automatically set payment method to Credit/Debit Card
  useEffect(() => {
    if (donationType === "recurring" && donationFormik?.values?.method !== "Credit/Debit Card") {
      donationFormik.setFieldValue("method", "Credit/Debit Card");
      setPaymentMethod("Credit/Debit Card");
    }
  }, [donationType]);

  const handleCustomAmount = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    if ((value.match(/\./g) || []).length > 1) return;

    setSelectedAmount(null);
    donationFormik.setFieldValue("amount", value);
    // Clear error in real-time when valid data is entered
    if (value && parseFloat(value) > 0) {
      donationFormik.setFieldError("amount", undefined);
    }
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    donationFormik.setFieldValue("amount", amount);
  };

  const handleCardChange = (newCard) => {
    setSelectedCard(newCard);
    if (newCard) {
      donationFormik.setFieldError("billing_id", undefined);
    }
    // Extract and set card details when card is selected
    if (newCard && typeof newCard === 'object') {
      // Set billing_id and customer_vault_id
      if (newCard["@attributes"]?.id) {
        donationFormik.setFieldValue("billing_id", newCard["@attributes"].id);
      } else if (newCard.billing_id) {
        donationFormik.setFieldValue("billing_id", newCard.billing_id);
      }
      
      if (newCard.customer_vault_id) {
        donationFormik.setFieldValue("customer_vault_id", newCard.customer_vault_id);
      } else if (newCard["@attributes"]?.customer_vault_id) {
        donationFormik.setFieldValue("customer_vault_id", newCard["@attributes"].customer_vault_id);
      }
      
      // Extract card_type from various possible fields
      const cardType = newCard.cardType || 
                      newCard.card_type || 
                      newCard.cc_type || 
                      newCard["@attributes"]?.cc_type ||
                      newCard["@attributes"]?.cardType ||
                      "";
      if (cardType) {
        donationFormik.setFieldValue("card_type", cardType);
      }
      
      // Extract cc_number from various possible fields
      const ccNumber = newCard.cc_number || 
                      newCard["@attributes"]?.cc_number || 
                      newCard.ccnumber || 
                      newCard["@attributes"]?.ccnumber || 
                      "";
      if (ccNumber) {
        donationFormik.setFieldValue("cc_number", ccNumber);
      }
    } else {
      // Clear card fields when card is deselected
      donationFormik.setFieldValue("billing_id", "");
      donationFormik.setFieldValue("customer_vault_id", "");
      donationFormik.setFieldValue("card_type", "");
      donationFormik.setFieldValue("cc_number", "");
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
              gap: 3,
            }}
          >
            <CircularProgress size={80} color="inherit" />
            <Typography
              variant="h5"
              sx={{
                color: primaryBlue,
                fontWeight: 600,
                textAlign: "center",
                animation: `${fadeInAnimation} 0.5s ease-out`,
              }}
            >
              Processing Your Donation...
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: primaryBlue,
                opacity: 0.8,
                textAlign: "center",
                maxWidth: "400px",
              }}
            >
              Please wait while we process your donation...
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            <Grid size={12}>
              <StyledCard>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    color: primaryBlue,
                    fontWeight: 700,
                    textAlign: "center",
                    mb: 4,
                    animation: `${fadeInAnimation} 0.5s ease-out`,
                  }}
                >
                  Make a Donation
                </Typography>
                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    textAlign: "center",
                    mb: 4,
                    fontSize: "1.1rem",
                    color: primaryBlue,
                    opacity: 0.8,
                  }}
                >
                  Your generosity makes a difference. Thank you for your
                  support!
                </Typography>

                <form onSubmit={donationFormik.handleSubmit} noValidate>
                  {/* Donation Type Selection */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        color: primaryBlue,
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span>Donation Frequency</span>
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid size={6}>
                        <Button
                          onClick={() => {
                            setDonationType("once");
                            donationFormik.setFieldValue("frequency", "");
                            donationFormik.setFieldValue("frequency_interval", "");
                            donationFormik.setFieldValue("day_of_month", "");
                            donationFormik.setFieldValue("weekday", "");
                            donationFormik.setFieldValue("month", "");
                            donationFormik.setFieldValue("day_of_year", "");
                            donationFormik.setFieldValue("days_after_quarter", "");
                          }}
                          fullWidth
                          sx={{
                            height: "60px",
                            borderRadius: "12px",
                            border: donationType === "once" ? "2px solid #0f2b46" : "2px solid #E5E9F2",
                            backgroundColor: donationType === "once" ? "#F0F4F8" : "white",
                            color: donationType === "once" ? "#0f2b46" : "#6B7280",
                            fontWeight: donationType === "once" ? 600 : 500,
                            fontSize: "16px",
                            textTransform: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            "&:hover": {
                              backgroundColor: donationType === "once" ? "#F0F4F8" : "#F8FAFC",
                              borderColor: "#0f2b46",
                            },
                          }}
                        >
                          {donationType === "once" && (
                            <FavoriteIcon sx={{ color: "#dc3545", fontSize: "24px" }} />
                          )}
                          One-Time Donation
                        </Button>
                      </Grid>
                      <Grid size={6}>
                        <Button
                          onClick={() => {
                            setDonationType("recurring");
                            if (!donationFormik.values.frequency) {
                              donationFormik.setFieldValue("frequency", "Monthly");
                            }
                          }}
                          fullWidth
                          sx={{
                            height: "60px",
                            borderRadius: "12px",
                            border: donationType === "recurring" ? "2px solid #0f2b46" : "2px solid #E5E9F2",
                            backgroundColor: donationType === "recurring" ? "#F0F4F8" : "white",
                            color: donationType === "recurring" ? "#0f2b46" : "#6B7280",
                            fontWeight: donationType === "recurring" ? 600 : 500,
                            fontSize: "16px",
                            textTransform: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            "&:hover": {
                              backgroundColor: donationType === "recurring" ? "#F0F4F8" : "#F8FAFC",
                              borderColor: "#0f2b46",
                            },
                          }}
                        >
                          {donationType === "recurring" && (
                            <FavoriteIcon sx={{ color: "#dc3545", fontSize: "24px" }} />
                          )}
                         Recurring Donation
                        </Button>
                      </Grid>
                    </Grid>
                    {/* {donationType === "recurring" && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          borderRadius: "12px",
                          backgroundColor: "#FFF5F5",
                          border: "1px solid #FED7D7",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "50%",
                            backgroundColor: "#C53030",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        >
                          <InfoIcon
                            sx={{
                              color: "white",
                              fontSize: "18px",
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              color: "#C53030",
                              fontWeight: 600,
                              fontSize: "14px",
                              mb: 0.5,
                            }}
                          >
                            Thanks for making a lasting difference for nature
                          </Typography>
                          <Typography
                            sx={{
                              color: "#C53030",
                              fontSize: "13px",
                              opacity: 0.9,
                            }}
                          >
                            {(() => {
                              const frequency = donationFormik?.values?.frequency;
                              if (frequency === "Yearly") {
                                return "Giving yearly is an easy, efficient, and effective way to protect the planet";
                              } else if (frequency === "Quarterly") {
                                return "Giving quarterly is an easy, efficient, and effective way to protect the planet";
                              } else if (frequency === "Weekly" || frequency === "Every N Weeks") {
                                return "Giving weekly is an easy, efficient, and effective way to protect the planet";
                              } else if (frequency === "Monthly" || frequency === "Every N Months") {
                                return "Giving monthly is an easy, efficient, and effective way to protect the planet";
                              } else {
                                return "Giving monthly is an easy, efficient, and effective way to protect the planet";
                              }
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    )} */}
                  </Box>

                  {/* Frequency Selection for Recurring Donations */}
                  {donationType === "recurring" && (
                    <Box sx={{ mb: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 3,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: primaryBlue,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>Select Frequency</span>
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography
                                sx={{
                                  color: "white",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  mb: 1,
                                }}
                              >
                                Recurring Donation Benefits:
                              </Typography>
                              <Typography
                                sx={{
                                  color: "white",
                                  fontSize: "11px",
                                  mb: 0.5,
                                }}
                              >
                                • Consistent support for our mission
                              </Typography>
                              <Typography
                                sx={{
                                  color: "white",
                                  fontSize: "11px",
                                  mb: 0.5,
                                }}
                              >
                                • Easy and convenient automatic payments
                              </Typography>
                              <Typography
                                sx={{
                                  color: "white",
                                  fontSize: "11px",
                                  mb: 0.5,
                                }}
                              >
                                • Make a lasting impact over time
                              </Typography>
                              <Typography
                                sx={{
                                  color: "white",
                                  fontSize: "11px",
                                }}
                              >
                                • Help us plan and budget effectively
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: "rgba(0, 0, 0, 0.9)",
                                maxWidth: "300px",
                                borderRadius: "8px",
                                padding: "12px",
                              },
                            },
                            arrow: {
                              sx: {
                                color: "rgba(0, 0, 0, 0.9)",
                              },
                            },
                          }}
                        >
                          <IconButton
                            size="small"
                            sx={{
                              color: primaryBlue,
                              "&:hover": {
                                backgroundColor: "rgba(41, 179, 226, 0.1)",
                              },
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {(() => {
                        const hasFrequencyError = (submitAttempted || donationFormik?.touched?.frequency) && (!donationFormik?.values?.frequency || donationFormik?.values?.frequency === "");
                        const hasValue = donationFormik?.values?.frequency && donationFormik?.values?.frequency !== "";
                        return (
                          <StyledDropdown>
                            <Dropdown
                              isOpen={frequencyDropdownOpen}
                              toggle={() => {
                                const wasOpen = frequencyDropdownOpen;
                                setFrequencyDropdownOpen(!frequencyDropdownOpen);
                                if (wasOpen && (!donationFormik?.values?.frequency || donationFormik?.values?.frequency === "")) {
                                  setTimeout(() => {
                                    donationFormik.setFieldTouched("frequency", true);
                                  }, 0);
                                }
                              }}
                            >
                              <DropdownToggle
                                caret
                                className={`dropdown-toggle ${hasFrequencyError ? "error-state" : ""}`}
                              >
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, paddingRight: hasValue ? "30px" : "0" }}>
                                  {donationFormik?.values?.frequency || "Select Frequency *"}
                                </span>
                                {hasValue && (
                                  <CloseIcon
                                    style={{
                                      fontSize: "18px",
                                      cursor: "pointer",
                                      position: "absolute",
                                      right: "28px",
                                      zIndex: 1,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      donationFormik.setFieldValue("frequency", "");
                                      donationFormik.setFieldValue("frequency_interval", "");
                                      donationFormik.setFieldValue("day_of_month", "");
                                      donationFormik.setFieldValue("weekday", "");
                                      donationFormik.setFieldValue("month", "");
                                      donationFormik.setFieldValue("day_of_year", "");
                                      donationFormik.setFieldValue("days_after_quarter", "");
                                      donationFormik.setFieldTouched("frequency", false);
                                      donationFormik.setFieldError("frequency", undefined);
                                    }}
                                  />
                                )}
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu">
                                {["Yearly", "Monthly", "Every N Months", "Weekly", "Every N Weeks", "Quarterly"].map((option) => (
                                  <DropdownItem
                                    key={option}
                                    className="dropdown-item"
                                    onClick={() => {
                                      donationFormik.setFieldValue("frequency", option);
                                      donationFormik.setFieldValue("frequency_interval", "");
                                      donationFormik.setFieldValue("day_of_month", "");
                                      donationFormik.setFieldValue("weekday", "");
                                      donationFormik.setFieldValue("month", "");
                                      donationFormik.setFieldValue("day_of_year", "");
                                      donationFormik.setFieldValue("days_after_quarter", "");
                                      donationFormik.setFieldTouched("frequency", true);
                                      if (option) {
                                        donationFormik.setFieldError("frequency", undefined);
                                      }
                                    }}
                                  >
                                    {option}
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </Dropdown>
                          </StyledDropdown>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Frequency-specific fields */}
                  {donationType === "recurring" && (donationFormik?.values?.frequency === "Every N Months" || donationFormik?.values?.frequency === "Every N Weeks") && (
                    <Box sx={{ mb: 3 }}>
                      <InputText
                        fullWidth
                        type="number"
                        label="Enter interval *"
                        value={donationFormik?.values?.frequency_interval || ""}
                        onChange={(e) => {
                          let value = parseInt(e.target.value, 10) || "";
                          let maxValue = donationFormik?.values?.frequency === "Every N Months" ? 12 : 4;
                          if (value > maxValue) value = maxValue;
                          if (value < 1) value = 1;
                          donationFormik.setFieldValue("frequency_interval", value);
                          if (value && value >= 1) {
                            donationFormik.setFieldError("frequency_interval", undefined);
                          }
                        }}
                        onBlur={() => {
                          donationFormik.setFieldTouched("frequency_interval", true);
                        }}
                        placeholder="Enter interval"
                        error={
                          (submitAttempted || donationFormik?.touched?.frequency_interval) && (!donationFormik?.values?.frequency_interval || donationFormik?.values?.frequency_interval === "" || Boolean(donationFormik?.errors?.frequency_interval))
                        }
                        helperText={undefined}
                        inputProps={{
                          min: 1,
                          max: donationFormik?.values?.frequency === "Every N Months" ? 12 : 4,
                        }}
                      />
                    </Box>
                  )}

                  {donationType === "recurring" && (donationFormik?.values?.frequency === "Monthly" || donationFormik?.values?.frequency === "Every N Months") && (
                    <Box sx={{ mb: 3 }}>
                      <InputText
                        fullWidth
                        type="number"
                        label="Enter day of month *"
                        value={donationFormik?.values?.day_of_month || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || (value >= 1 && value <= 28)) {
                            donationFormik.setFieldValue("day_of_month", value);
                            if (value && parseInt(value, 10) >= 1 && parseInt(value, 10) <= 28) {
                              donationFormik.setFieldError("day_of_month", undefined);
                            }
                          }
                        }}
                        onBlur={() => {
                          donationFormik.setFieldTouched("day_of_month", true);
                        }}
                        placeholder="Enter day of the month (1-28)"
                        error={
                          (submitAttempted || donationFormik?.touched?.day_of_month) && (!donationFormik?.values?.day_of_month || donationFormik?.values?.day_of_month === "" || Boolean(donationFormik?.errors?.day_of_month))
                        }
                        helperText={undefined}
                        inputProps={{
                          min: 1,
                          max: 28,
                        }}
                      />
                    </Box>
                  )}

                  {donationType === "recurring" && (donationFormik?.values?.frequency === "Every N Weeks" || donationFormik?.values?.frequency === "Weekly") && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          color: primaryBlue,
                          mb: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span>Select Day of the Week</span>
                      </Typography>
                      {(() => {
                        const hasWeekdayError = (submitAttempted || donationFormik?.touched?.weekday) && (!donationFormik?.values?.weekday || donationFormik?.values?.weekday === "");
                        const hasValue = donationFormik?.values?.weekday && donationFormik?.values?.weekday !== "";
                        return (
                          <StyledDropdown>
                            <Dropdown
                              isOpen={weekdayDropdownOpen}
                              toggle={() => {
                                const wasOpen = weekdayDropdownOpen;
                                setWeekdayDropdownOpen(!weekdayDropdownOpen);
                                if (wasOpen && (!donationFormik?.values?.weekday || donationFormik?.values?.weekday === "")) {
                                  setTimeout(() => {
                                    donationFormik.setFieldTouched("weekday", true);
                                  }, 0);
                                }
                              }}
                            >
                              <DropdownToggle
                                caret
                                className={`dropdown-toggle ${hasWeekdayError ? "error-state" : ""}`}
                              >
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, paddingRight: hasValue ? "30px" : "0" }}>
                                  {donationFormik?.values?.weekday || "Select Day of the Week *"}
                                </span>
                                {hasValue && (
                                  <CloseIcon
                                    style={{
                                      fontSize: "18px",
                                      cursor: "pointer",
                                      position: "absolute",
                                      right: "28px",
                                      zIndex: 1,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      donationFormik.setFieldValue("weekday", "");
                                      donationFormik.setFieldTouched("weekday", false);
                                      donationFormik.setFieldError("weekday", undefined);
                                    }}
                                  />
                                )}
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu">
                                {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day) => (
                                  <DropdownItem
                                    key={day}
                                    className="dropdown-item"
                                    onClick={() => {
                                      donationFormik.setFieldValue("weekday", day);
                                      donationFormik.setFieldTouched("weekday", true);
                                      if (day) {
                                        donationFormik.setFieldError("weekday", undefined);
                                      }
                                    }}
                                  >
                                    {day}
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </Dropdown>
                          </StyledDropdown>
                        );
                      })()}
                    </Box>
                  )}

                  {donationType === "recurring" && donationFormik?.values?.frequency === "Yearly" && (
                    <Box sx={{ mb: 3 }}>
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <InputText
                            fullWidth
                            type="number"
                            label="Enter day *"
                            value={donationFormik?.values?.day_of_year || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || (value >= 1 && value <= 28)) {
                                donationFormik.setFieldValue("day_of_year", value);
                                if (value && parseInt(value, 10) >= 1 && parseInt(value, 10) <= 28) {
                                  donationFormik.setFieldError("day_of_year", undefined);
                                }
                              }
                            }}
                            onBlur={() => {
                              donationFormik.setFieldTouched("day_of_year", true);
                            }}
                            placeholder="Enter day (1-28)"
                            error={
                              (submitAttempted || donationFormik?.touched?.day_of_year) && (!donationFormik?.values?.day_of_year || donationFormik?.values?.day_of_year === "" || Boolean(donationFormik?.errors?.day_of_year))
                            }
                            helperText={undefined}
                            inputProps={{
                              min: 1,
                              max: 28,
                            }}
                          />
                        </Grid>
                        <Grid size={6}>
                          <InputText
                            fullWidth
                            type="number"
                            label="Enter month *"
                            value={donationFormik?.values?.month || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || (value >= 1 && value <= 12)) {
                                donationFormik.setFieldValue("month", value);
                                if (value && parseInt(value, 10) >= 1 && parseInt(value, 10) <= 12) {
                                  donationFormik.setFieldError("month", undefined);
                                }
                              }
                            }}
                            onBlur={() => {
                              donationFormik.setFieldTouched("month", true);
                            }}
                            placeholder="Enter month (1-12)"
                            error={
                              (submitAttempted || donationFormik?.touched?.month) && (!donationFormik?.values?.month || donationFormik?.values?.month === "" || Boolean(donationFormik?.errors?.month))
                            }
                            helperText={undefined}
                            inputProps={{
                              min: 1,
                              max: 12,
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {donationType === "recurring" && donationFormik?.values?.frequency === "Quarterly" && (
                    <Box sx={{ mb: 3 }}>
                      <InputText
                        fullWidth
                        type="number"
                        label="Enter days after quarter start *"
                        value={donationFormik?.values?.days_after_quarter || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || (value >= 0 && value <= 90)) {
                            donationFormik.setFieldValue("days_after_quarter", value);
                            if (value !== "" && value !== null && value !== undefined && parseInt(value, 10) >= 0 && parseInt(value, 10) <= 90) {
                              donationFormik.setFieldError("days_after_quarter", undefined);
                            }
                          }
                        }}
                        onBlur={() => {
                          donationFormik.setFieldTouched("days_after_quarter", true);
                        }}
                        placeholder="Enter days after quarter start (0-90)"
                        error={
                          (submitAttempted || donationFormik?.touched?.days_after_quarter) && (donationFormik?.values?.days_after_quarter === "" || donationFormik?.values?.days_after_quarter === null || donationFormik?.values?.days_after_quarter === undefined || Boolean(donationFormik?.errors?.days_after_quarter))
                        }
                        helperText={undefined}
                        inputProps={{
                          min: 0,
                          max: 90,
                        }}
                      />
                    </Box>
                  )}

                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        color: primaryBlue,
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span>Select Donation Amount</span>
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {presetAmounts.map((amount) => {
                        const getSuffix = () => {
                          if (donationType === "once") return "";
                          const frequency = donationFormik?.values?.frequency;
                          if (frequency === "Monthly" || frequency === "Every N Months") return "/mo";
                          if (frequency === "Weekly" || frequency === "Every N Weeks") return "/wk";
                          if (frequency === "Quarterly") return "/qtr";
                          if (frequency === "Yearly") return "/yr";
                          return "/mo";
                        };
                        return (
                          <Grid
                            key={amount}
                            size={{
                              xs: 6,
                              sm: 4
                            }}>
                            <DonationAmountButton
                              fullWidth
                              selected={selectedAmount === amount}
                              onClick={() => handleAmountSelect(amount)}
                            >
                              ${amount}{getSuffix()}
                            </DonationAmountButton>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <DollerInput
                      name="amount"
                      label="Amount"
                      required
                      value={donationFormik?.values?.amount}
                      onChange={handleCustomAmount}
                      onBlur={() => {
                        donationFormik.setFieldTouched("amount", true);
                      }}
                      error={
                        (submitAttempted || donationFormik?.touched?.amount) &&
                        (!donationFormik?.values?.amount ||
                          donationFormik?.values?.amount === "" ||
                          parseFloat(donationFormik?.values?.amount || "0") <= 0 ||
                          Boolean(donationFormik?.errors?.amount))
                      }
                      helperText={undefined}
                      showErrorOnly={true}
                      className="w-100"
                    />
                  </Box>

                  {tokenDecode?.UserId && (
                    <Box sx={{ mt: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            color: primaryBlue,
                            mb: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>Donation Method</span>
                        </Typography>
                      </Box>

                      <Grid container spacing={2} className="mt-2">
                        <Grid
                          className="mb-2"
                          size={{
                            xs: 12,
                            sm: 6,
                            md: 6
                          }}>
                          <Button
                            disabled={!creditCardEnabled}
                            onClick={() => {
                              if (!creditCardEnabled) return;
                              donationFormik?.setFieldValue(
                                "method",
                                "Credit/Debit Card"
                              );
                              setPaymentMethod("Credit/Debit Card");
                            }}
                            style={{
                              width: "100%",
                              height: "60px",
                              padding: "8px",
                              borderRadius: "8px",
                              border:
                                paymentMethod === "Credit/Debit Card"
                                  ? "2px solid #0f2b46"
                                  : "2px solid #E5E9F2",
                              background:
                                paymentMethod === "Credit/Debit Card"
                                  ? "#F0F4F8"
                                  : "white",
                              color:
                                paymentMethod === "Credit/Debit Card"
                                  ? "#0f2b46"
                                  : "#6B7280",
                              fontWeight:
                                paymentMethod === "Credit/Debit Card"
                                  ? "600"
                                  : "500",
                              fontSize: "14px",
                              transition: "all 0.2s ease",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              textTransform: "none",
                              lineHeight: "1.2",
                              opacity: creditCardEnabled ? 1 : 0.6,
                              cursor: creditCardEnabled ? "pointer" : "not-allowed",
                            }}
                            onMouseEnter={(e) => {
                              if (creditCardEnabled && paymentMethod !== "Credit/Debit Card") {
                                e.target.style.borderColor = "#0f2b46";
                                e.target.style.background = "#F8FAFC";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (creditCardEnabled && paymentMethod !== "Credit/Debit Card") {
                                e.target.style.borderColor = "#E5E9F2";
                                e.target.style.background = "white";
                              }
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 1, flexWrap: "wrap" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  style={{ minWidth: "24px", minHeight: "24px" }}
                                >
                                  <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                                </svg>
                                Donate with Card on File
                              </Box>
                              {!creditCardEnabled && (
                                <Typography component="span" sx={{ fontSize: "11px", color: "#B45309", fontWeight: 500, flexShrink: 0 }}>
                                  For this company, credit/debit card is disabled.
                                </Typography>
                              )}
                            </Box>
                          </Button>
                        </Grid>

                        {donationType === "once" && (
                          <Grid
                            className="mb-2"
                            size={{
                              xs: 12,
                              sm: 6,
                              md: 6
                            }}>
                            <Button
                              disabled={!achEnabled}
                              onClick={() => {
                                if (!achEnabled) return;
                                donationFormik?.setFieldValue(
                                  "method",
                                  "ACH Bank Payment"
                                );
                                setPaymentMethod("ACH Bank Payment");
                              }}
                            style={{
                              width: "100%",
                              height: "60px",
                              padding: "8px",
                              borderRadius: "8px",
                              border:
                                paymentMethod === "ACH Bank Payment"
                                  ? "2px solid #0f2b46"
                                  : "2px solid #E5E9F2",
                              background:
                                paymentMethod === "ACH Bank Payment"
                                  ? "#F0F4F8"
                                  : "white",
                              color:
                                paymentMethod === "ACH Bank Payment"
                                  ? "#0f2b46"
                                  : "#6B7280",
                              fontWeight:
                                paymentMethod === "ACH Bank Payment"
                                  ? "600"
                                  : "500",
                              fontSize: "14px",
                              transition: "all 0.2s ease",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              textTransform: "none",
                              lineHeight: "1.2",
                              opacity: achEnabled ? 1 : 0.6,
                              cursor: achEnabled ? "pointer" : "not-allowed",
                            }}
                            onMouseEnter={(e) => {
                              if (achEnabled && paymentMethod !== "ACH Bank Payment") {
                                e.target.style.borderColor = "#0f2b46";
                                e.target.style.background = "#F8FAFC";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (achEnabled && paymentMethod !== "ACH Bank Payment") {
                                e.target.style.borderColor = "#E5E9F2";
                                e.target.style.background = "white";
                              }
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 1, flexWrap: "wrap" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  style={{ minWidth: "24px", minHeight: "24px" }}
                                >
                                  <path d="M12 2L2 8v2h20V8L12 2zm0 3l6 3H6l6-3zM4 12v8h4v-8H4zm6 0v8h4v-8h-4zm6 0v8h4v-8h-4zM2 22h20v-2H2v2z" />
                                </svg>
                                Donate with ACH Bank Payment
                              </Box>
                              {!achEnabled && (
                                <Typography component="span" sx={{ fontSize: "11px", color: "#B45309", fontWeight: 500, flexShrink: 0 }}>
                                  For this company, ACH is disabled.
                                </Typography>
                              )}
                            </Box>
                          </Button>
                        </Grid>
                        )}
                        {donationType === "once" && gateway !== "DEJAVOO" &&
                          tokenDecode.Gateway !== "PAYA" && (
                            <Grid
                              className="mb-2"
                              size={{
                                xs: 12,
                                sm: 6,
                                md: 6
                              }}>
                              <Button
                                onClick={() => {
                                  donationFormik?.setFieldValue(
                                    "method",
                                    "Apple Pay"
                                  );
                                  setPaymentMethod("Apple Pay");
                                }}
                                style={{
                                  width: "100%",
                                  height: "60px",
                                  padding: "8px",
                                  borderRadius: "8px",
                                  border:
                                    paymentMethod === "Apple Pay"
                                      ? "2px solid #0f2b46"
                                      : "2px solid #E5E9F2",
                                  background:
                                    paymentMethod === "Apple Pay"
                                      ? "#F0F4F8"
                                      : "white",
                                  color:
                                    paymentMethod === "Apple Pay"
                                      ? "#0f2b46"
                                      : "#6B7280",
                                  fontWeight:
                                    paymentMethod === "Apple Pay"
                                      ? "600"
                                      : "500",
                                  fontSize: "14px",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  gap: "8px",
                                  textTransform: "none",
                                  lineHeight: "1.2",
                                }}
                                onMouseEnter={(e) => {
                                  if (paymentMethod !== "Apple Pay") {
                                    e.target.style.borderColor = "#0f2b46";
                                    e.target.style.background = "#F8FAFC";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (paymentMethod !== "Apple Pay") {
                                    e.target.style.borderColor = "#E5E9F2";
                                    e.target.style.background = "white";
                                  }
                                }}
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  style={{
                                    minWidth: "24px",
                                    minHeight: "24px",
                                  }}
                                >
                                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                </svg>
                                Donate with Apple Pay
                              </Button>
                            </Grid>
                          )}
                        {donationType === "once" && gateway !== "DEJAVOO" &&
                          tokenDecode.Gateway !== "PAYA" && (
                            <Grid
                              className="mb-2"
                              size={{
                                xs: 12,
                                sm: 6,
                                md: 6
                              }}>
                              <Button
                                onClick={() => {
                                  donationFormik?.setFieldValue(
                                    "method",
                                    "Google Pay"
                                  );
                                  setPaymentMethod("Google Pay");
                                }}
                                style={{
                                  width: "100%",
                                  height: "60px",
                                  padding: "8px",
                                  borderRadius: "8px",
                                  border:
                                    paymentMethod === "Google Pay"
                                      ? "2px solid #0f2b46"
                                      : "2px solid #E5E9F2",
                                  background:
                                    paymentMethod === "Google Pay"
                                      ? "#F0F4F8"
                                      : "white",
                                  color:
                                    paymentMethod === "Google Pay"
                                      ? "#0f2b46"
                                      : "#6B7280",
                                  fontWeight:
                                    paymentMethod === "Google Pay"
                                      ? "600"
                                      : "500",
                                  fontSize: "14px",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  gap: "8px",
                                  textTransform: "none",
                                  lineHeight: "1.2",
                                }}
                                onMouseEnter={(e) => {
                                  if (paymentMethod !== "Google Pay") {
                                    e.target.style.borderColor = "#0f2b46";
                                    e.target.style.background = "#F8FAFC";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (paymentMethod !== "Google Pay") {
                                    e.target.style.borderColor = "#E5E9F2";
                                    e.target.style.background = "white";
                                  }
                                }}
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  style={{
                                    minWidth: "24px",
                                    minHeight: "24px",
                                  }}
                                >
                                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                </svg>
                                Donate with Google Pay
                              </Button>
                            </Grid>
                          )}
                      </Grid>
                    </Box>
                  )}

                  {tokenDecode?.UserId && (
                    <Box sx={{ mt: 4 }}>
                      {(() => {
                        const isCardOrAch =
                          donationFormik?.values?.method === "Credit/Debit Card" ||
                          donationFormik?.values?.method === "ACH Bank Payment";
                        const showPaymentError =
                          isCardOrAch &&
                          donationFormik?.errors?.billing_id &&
                          (donationFormik?.touched?.billing_id || submitAttempted);
                        return (
                          <Box>
                            {donationFormik?.values?.method === "Apple Pay" ? (
                        <ApplePayPayment
                          paymentFormik={donationFormik}
                          CustomerId={tokenDecode?.UserId}
                        />
                      ) : donationFormik?.values?.method === "Google Pay" ? (
                        <GooglePayPayment
                          paymentFormik={donationFormik}
                          CustomerId={tokenDecode?.UserId}
                        />
                      ) : donationFormik?.values?.method ===
                        "ACH Bank Payment" ? (
                        <AchPayment
                          paymentFormik={donationFormik}
                          selectedAccount={selectedCard}
                          setSelectedAccount={(account) => {
                            setSelectedCard(account);
                            if (account) donationFormik.setFieldError("billing_id", undefined);
                          }}
                          companyData={companyData}
                          CustomerId={tokenDecode?.UserId}
                          tokenDecode={tokenDecode}
                        />
                      ) : (
                        <>
                          {!tokenDecode ? (
                            <div>Loading company data...</div>
                          ) : !tokenDecode.Gateway ? (
                            <div>Note: No payment gateway configured</div>
                          ) : gateway === "PAYA" ? (
                            <PayaAddCardForm
                              onSuccess={(cardData) => {
                                handleCardChange(cardData);
                              }}
                              onCancel={() => {
                                // Handle cancel
                              }}
                              CustomerId={tokenDecode?.UserId}
                              CompanyId={tokenDecode?.CompanyId}
                            />
                          ) : (
                            <CardPayment
                              paymentFormik={donationFormik}
                              selectedCard={selectedCard}
                              setSelectedCard={(card) => {
                                handleCardChange(card);
                                // Also set card_type and cc_number directly in formik when CardPayment sets the card
                                if (card && typeof card === 'object') {
                                  if (card.cardType || card.card_type || card.cc_type) {
                                    donationFormik.setFieldValue("card_type", card.cardType || card.card_type || card.cc_type || "");
                                  }
                                  if (card.cc_number) {
                                    donationFormik.setFieldValue("cc_number", card.cc_number || "");
                                  }
                                  if (card.customer_vault_id) {
                                    donationFormik.setFieldValue("customer_vault_id", card.customer_vault_id);
                                  }
                                }
                              }}
                              CompanyId={tokenDecode?.CompanyId}
                              CustomerId={tokenDecode?.UserId}
                              tokenDecode={{ Gateway: gateway || tokenDecode?.Gateway, CompanyId: tokenDecode?.CompanyId }}
                            />
                          )}
                        </>
                      )}
                            {showPaymentError && (
                              <Typography
                                variant="body2"
                                sx={{ color: "#d32f2f", mt: 1.5, fontWeight: 500 }}
                              >
                                {donationFormik?.errors?.billing_id}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}

                  {donationFormik?.values?.method === "Credit/Debit Card" &&
                    donationFormik?.values?.amount &&
                    parseFloat(donationFormik?.values?.amount) > 0 && (() => {
                      const totals = calculatePaymentTotals();
                      // Show Payment Summary only if fee is applied OR tax > 0
                      const hasFee = totals.showConvenienceFee;
                      const hasTax = totals.tax > 0;
                      const shouldShowSummary = hasFee || hasTax;
                      if (!shouldShowSummary) return null;
                      return (
                        <Box sx={{ mb: 4 }}>
                          <Card
                            style={{
                              padding: "15px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "8px",
                            }}
                          >
                            {(() => {
                              return (
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    style={{
                                      color: "#0f2b46",
                                      fontWeight: "600",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    Payment Summary
                                  </Typography>
                                  {/* Cash Discount: Show Cash Price and Card Price only if fee amount > 0 */}
                                  {totals.isCashDiscount && cardFeeConfig?.FeeAmount > 0 ? (
                                    <>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "#666", fontWeight: 500 }}
                                        >
                                          Cash Price:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "#0f2b46", fontWeight: 600 }}
                                        >
                                          ${totals.cashPrice.toFixed(2)}
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "#666", fontWeight: 500 }}
                                        >
                                          Card Price:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "#0f2b46", fontWeight: 600 }}
                                        >
                                          ${(() => {
                                            // Use displayed (rounded) values: Cash Price + displayed Convenience Fee
                                            const displayedCashPrice = parseFloat(totals.cashPrice.toFixed(2));
                                            const displayedConvenienceFee = parseFloat(totals.convenienceFee.toFixed(2));
                                            return (displayedCashPrice + displayedConvenienceFee).toFixed(2);
                                          })()}
                                        </Typography>
                                      </Box>
                                    </>
                                  ) : (
                                    <>
                                      {/* Surcharge: Show Subtotal and Convenience Fee */}
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "#666", fontWeight: 500 }}
                                        >
                                          Subtotal:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "#0f2b46", fontWeight: 600 }}
                                        >
                                          ${totals.subtotal.toFixed(2)}
                                        </Typography>
                                      </Box>
                                      {totals.showConvenienceFee && totals.isSurcharge && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            pt: 1,
                                            borderTop: "1px solid #e1e5e9",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{ color: "#666", fontWeight: 500 }}
                                          >
                                            {cardFeeConfig?.FeeLabel || "Convenience Fee"}:
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{ color: "#0f2b46", fontWeight: 600 }}
                                          >
                                            ${totals.convenienceFee.toFixed(2)}
                                          </Typography>
                                        </Box>
                                      )}
                                    </>
                                  )}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      pt: 1.5,
                                      borderTop: "2px solid #0f2b46",
                                      mt: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        color: "#0f2b46",
                                        fontWeight: 700,
                                        fontSize: "16px",
                                      }}
                                    >
                                      Total:
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        color: "#0f2b46",
                                        fontWeight: 700,
                                        fontSize: "18px",
                                      }}
                                    >
                                      ${(() => {
                                        // Use displayed (rounded) values to match what user sees
                                        const displayedSubtotal = parseFloat(totals.subtotal.toFixed(2));
                                        const displayedConvenienceFee = parseFloat(totals.convenienceFee.toFixed(2));
                                        // For Cash Discount: Total = displayed Card Price (Cash Price + displayed Convenience Fee)
                                        // For Surcharge: Total = displayed Subtotal + displayed Convenience Fee
                                        const displayedTotal = totals.isCashDiscount
                                          ? parseFloat(totals.cashPrice.toFixed(2)) + displayedConvenienceFee
                                          : displayedSubtotal + displayedConvenienceFee;
                                        return displayedTotal.toFixed(2);
                                      })()}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })()}
                          </Card>
                        </Box>
                      );
                    })()}

                  {/* General Error Message */}
                  {submitAttempted && Boolean(
                    (!donationFormik?.values?.amount || 
                     donationFormik?.values?.amount === "" || 
                     parseFloat(donationFormik?.values?.amount || "0") <= 0) ||
                    (donationType === "recurring" && (!donationFormik?.values?.frequency || donationFormik?.values?.frequency === "")) ||
                    (donationType === "recurring" && (donationFormik?.values?.frequency === "Every N Months" || donationFormik?.values?.frequency === "Every N Weeks") && (!donationFormik?.values?.frequency_interval || donationFormik?.values?.frequency_interval === "")) ||
                    (donationType === "recurring" && (donationFormik?.values?.frequency === "Monthly" || donationFormik?.values?.frequency === "Every N Months") && (!donationFormik?.values?.day_of_month || donationFormik?.values?.day_of_month === "")) ||
                    (donationType === "recurring" && (donationFormik?.values?.frequency === "Weekly" || donationFormik?.values?.frequency === "Every N Weeks") && (!donationFormik?.values?.weekday || donationFormik?.values?.weekday === "")) ||
                    (donationType === "recurring" && donationFormik?.values?.frequency === "Yearly" && (!donationFormik?.values?.day_of_year || donationFormik?.values?.day_of_year === "" || !donationFormik?.values?.month || donationFormik?.values?.month === "")) ||
                    (donationType === "recurring" && donationFormik?.values?.frequency === "Quarterly" && (donationFormik?.values?.days_after_quarter === "" || donationFormik?.values?.days_after_quarter === null || donationFormik?.values?.days_after_quarter === undefined))
                  ) && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography
                        sx={{
                          color: "#dc3545",
                          fontSize: "14px",
                          fontWeight: 500,
                          textAlign: "center",
                        }}
                      >
                        Please correct the red boxes above
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 4 }}>
                    <SubmitButton
                      type="submit"
                      fullWidth
                      size="large"
                      disabled={
                        loading ||
                        donationFormik?.values?.method === "Apple Pay" ||
                        donationFormik?.values?.method === "Google Pay" ||
                        (donationFormik?.values?.method === "Credit/Debit Card" &&
                          donationFormik?.values?.amount &&
                          parseFloat(donationFormik?.values?.amount) > 0 &&
                          !selectedCard &&
                          !(donationFormik?.values?.billing_id != null && donationFormik?.values?.billing_id !== "")) ||
                        (donationFormik?.values?.method === "ACH Bank Payment" &&
                          donationFormik?.values?.amount &&
                          parseFloat(donationFormik?.values?.amount) > 0 &&
                          !(donationFormik?.values?.billing_id != null && donationFormik?.values?.billing_id !== ""))
                      }
                    >
                      <span>
                        {donationType === "recurring" 
                          ? "Set up Recurring Donation"
                          : donationFormik?.values?.method === "Credit/Debit Card" &&
                            donationFormik?.values?.amount &&
                            parseFloat(donationFormik?.values?.amount) > 0
                            ? (() => {
                                const totals = calculatePaymentTotals();
                                // Only show total amount if transaction fee is being applied
                                if (totals.showConvenienceFee && totals.convenienceFee > 0) {
                                  // Use displayed (rounded) values to match what user sees
                                  const displayedSubtotal = parseFloat(totals.subtotal.toFixed(2));
                                  const displayedConvenienceFee = parseFloat(totals.convenienceFee.toFixed(2));
                                  const displayedTotal = totals.isCashDiscount
                                    ? parseFloat(totals.cashPrice.toFixed(2)) + displayedConvenienceFee
                                    : displayedSubtotal + displayedConvenienceFee;
                                  return `Donate $${displayedTotal.toFixed(2)}`;
                                }
                                return "Complete Donation";
                              })()
                            : "Complete Donation"}
                      </span>
                    </SubmitButton>
                  </Box>
                </form>
              </StyledCard>
            </Grid>

          </Grid>
        )}
      </motion.div>
    </Container>
  );
};

export default MakeDonation;
